import { IconX } from "../layout/Icons";

export const MAX_ATTACHMENTS = 4;
/** Larger edges blow up the request body without helping the model — Anthropic/OpenRouter
 * downscale anything bigger internally anyway. 1600px keeps screenshots readable. */
const MAX_IMAGE_EDGE = 1600;

export interface Attachment {
  readonly id: string;
  readonly dataUrl: string;
  readonly name: string;
  readonly mime: string;
  readonly bytes: number;
}

export async function fileToAttachment(file: File): Promise<Attachment> {
  const id = `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const dataUrl = await downscaleImageToDataUrl(file);
  return {
    id,
    dataUrl,
    name: file.name || "image",
    mime: dataUrl.slice(5, dataUrl.indexOf(";")) || file.type || "image/png",
    bytes: Math.round((dataUrl.length - dataUrl.indexOf(",") - 1) * 0.75),
  };
}

/** Decode → draw to canvas at capped edge → re-encode as JPEG. Keeps PNGs that
 * fit under the cap as PNG to preserve transparency / pixel art screenshots. */
async function downscaleImageToDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return readAsDataUrl(file);
  const { width, height } = bitmap;
  const max = Math.max(width, height);
  const scale = max > MAX_IMAGE_EDGE ? MAX_IMAGE_EDGE / max : 1;
  if (scale === 1 && file.type === "image/png") {
    bitmap.close?.();
    return readAsDataUrl(file);
  }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d unavailable");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return canvas.toDataURL("image/jpeg", 0.85);
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error("read failed"));
    r.readAsDataURL(file);
  });
}

export function Thumbnail({ att, onRemove }: { att: Attachment; onRemove: () => void }) {
  return (
    <div
      style={{
        position: "relative",
        width: 56,
        height: 56,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid var(--border-default)",
        background: "var(--bg-canvas)",
      }}
      title={`${att.name} · ${formatBytes(att.bytes)}`}
    >
      <img
        src={att.dataUrl}
        alt={att.name}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
      <button
        type="button"
        aria-label={`Remove ${att.name}`}
        onClick={onRemove}
        style={{
          position: "absolute",
          top: 2,
          right: 2,
          width: 16,
          height: 16,
          borderRadius: 8,
          border: "none",
          background: "rgba(0,0,0,0.7)",
          color: "#fff",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          lineHeight: 1,
        }}
      >
        <IconX size={10} />
      </button>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function hasDraggedFiles(types: ReadonlyArray<string>): boolean {
  return types.includes("Files");
}

/** Pull image File[] off a paste event's clipboardData. */
export function extractPastedImages(items: DataTransferItemList): File[] {
  return Array.from(items)
    .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
    .map((it) => it.getAsFile())
    .filter((f): f is File => f !== null);
}
